import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiEdit, FiTrash } from "react-icons/fi";
import useClients from "@/features/client/hooks/useClients";
import useProjects from "@/features/project/hooks/useProjects";
import { useAuth } from "@/auth/useAuth";
import TaskActionsMenu from "./TaskActionsMenu";
import useTicketSuffix from "@/features/task/hooks/useTicketSuffix";
const TaskTable = ({ tasks, onEdit, onDelete, onChangeState, taskStates }) => {
    const [clientMap, setClientMap] = useState({});
    const { clients } = useClients(undefined, undefined, true);
    const [projectMap, setProjectMap] = useState({});
    const { projects } = useProjects(undefined, undefined, true);
    const { user } = useAuth();
    const { formatTicketCode } = useTicketSuffix();
    const [menuTask, setMenuTask] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const touchTimeout = useRef(null);
    const showMenu = (task, x, y) => {
        setMenuTask(task);
        setMenuPosition({ x, y });
    };
    const handleTouchStart = (e, task) => {
        touchTimeout.current = setTimeout(() => {
            const touch = e.touches[0];
            showMenu(task, touch.clientX, touch.clientY);
        }, 600);
    };
    const handleTouchEnd = () => {
        if (touchTimeout.current) {
            clearTimeout(touchTimeout.current);
            touchTimeout.current = null;
        }
    };
    const handleCloseMenu = () => setMenuTask(null);
    const customFieldColumns = useMemo(() => {
        const fieldMap = new Map();
        tasks.forEach(task => {
            task.custom_fields?.forEach(field => {
                if (field.is_active === false) {
                    return;
                }
                if (!fieldMap.has(field.code)) {
                    fieldMap.set(field.code, { code: field.code, label: field.label });
                }
            });
        });
        return Array.from(fieldMap.values());
    }, [tasks]);
    const formatCustomFieldValue = (field) => {
        if (!field || field.value === null || field.value === undefined) {
            return "";
        }
        const rawValue = field.value;
        switch (field.type) {
            case "boolean": {
                if (typeof rawValue === "boolean") {
                    return rawValue ? "Oui" : "Non";
                }
                if (typeof rawValue === "string") {
                    return rawValue.toLowerCase() === "true" ? "Oui" : "Non";
                }
                return "";
            }
            case "date": {
                const date = new Date(String(rawValue));
                if (!Number.isNaN(date.getTime())) {
                    return date.toLocaleDateString("fr-FR");
                }
                return String(rawValue);
            }
            case "select": {
                const optionLabel = field.options?.find(option => option.value === rawValue)?.label;
                return optionLabel ?? String(rawValue);
            }
            default:
                return String(rawValue);
        }
    };
    useEffect(() => {
        const fetchClientsAndProjetcs = async () => {
            const mapClient = {};
            tasks.forEach(task => {
                if (task.id) {
                    if (task.client) {
                        mapClient[task.id] = task.client;
                    }
                    else if (task.client_id) {
                        const foundClient = clients.find(cli => cli.id === task.client_id);
                        if (foundClient) {
                            mapClient[task.id] = foundClient;
                        }
                    }
                }
            });
            setClientMap(mapClient);
            const mapProject = {};
            tasks.forEach(task => {
                if (task.id) {
                    if (task.project) {
                        mapProject[task.id] = task.project;
                    }
                    else if (task.project_id) {
                        const foundProject = projects.find(proj => proj.id === task.project_id);
                        if (foundProject) {
                            mapProject[task.id] = foundProject;
                        }
                    }
                }
            });
            setProjectMap(mapProject);
        };
        if (tasks && tasks.length > 0) {
            fetchClientsAndProjetcs();
        }
    }, [clients, projects, tasks]);
    return (_jsxs("div", { className: "relative", children: [_jsxs("table", { className: "min-w-[700px] w-full table-auto border-collapse border bg-white border-gray-300", children: [_jsx("thead", { className: "hidden md:table-header-group bg-gray-200 text-sm", children: _jsxs("tr", { children: [_jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Ticket" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Titre" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "R\u00E9f. fonci\u00E8re" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Statut" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Priorit\u00E9" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Ville" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "\u00C9ch\u00E9ance" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Projet" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Client" }), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Parent" }), customFieldColumns.map(field => (_jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: field.label }, field.code))), _jsx("th", { className: "border px-4 py-2 whitespace-nowrap", children: "Actions" })] }) }), _jsx("tbody", { className: "text-sm", children: tasks.map((task) => (_jsxs("tr", { className: "flex flex-col md:table-row mb-4 md:mb-0 border rounded md:border-0 relative", onTouchStart: (e) => handleTouchStart(e, task), onTouchEnd: handleTouchEnd, children: [_jsxs("td", { className: "px-4 py-2 border-b md:border text-xs whitespace-nowrap", children: [_jsx("strong", { className: "md:hidden", children: "Ticket :" }), _jsx(Link, { to: `/tasks/${task.id}`, children: formatTicketCode(task.id) })] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "Titre :" }), " ", task.title] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "R\u00E9f. fonci\u00E8re :" }), " ", task.ref_fonciere] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "Statut :" }), " ", task.status?.label] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "Priorit\u00E9 :" }), " ", task.priority?.label] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "Ville :" }), " ", task.ville_label] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "\u00C9ch\u00E9ance :" }), " ", task.due_date ? new Date(task.due_date).toLocaleDateString('fr-FR') : ''] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "Projet :" }), " ", projectMap[task.id] ? projectMap[task.id].label : ""] }), _jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsx("strong", { className: "md:hidden", children: "Client :" }), " ", clientMap[task.id] ? clientMap[task.id].denomination : ""] }), _jsxs("td", { className: "px-4 py-2 border-b md:border text-xs whitespace-nowrap", children: [_jsx("strong", { className: "md:hidden", children: "Parent :" }), task.parent_id && (_jsx(Link, { to: `/tasks/${task.parent_id}`, children: formatTicketCode(task.parent_id) }))] }), customFieldColumns.map(field => {
                                    const taskField = task.custom_fields?.find(customField => customField.code === field.code);
                                    return (_jsxs("td", { className: "px-4 py-2 border-b md:border break-words", children: [_jsxs("strong", { className: "md:hidden", children: [field.label, " :"] }), " ", formatCustomFieldValue(taskField)] }, field.code));
                                }), _jsxs("td", { className: "px-4 py-2 md:border", children: [_jsx("strong", { className: "md:hidden", children: "Actions :" }), _jsx("button", { onClick: () => onEdit(task), className: "text-blue-500 hover:text-blue-700", children: _jsx(FiEdit, { size: 18 }) }), user?.role_id !== 4 && (_jsx("button", { onClick: () => onDelete(task.id ?? 0), className: "text-red-500 hover:text-red-700", children: _jsx(FiTrash, { size: 18 }) }))] }), menuTask?.id === task.id && (_jsx(TaskActionsMenu, { position: menuPosition, onEdit: () => {
                                        onEdit(task);
                                        handleCloseMenu();
                                    }, onDelete: () => {
                                        onDelete(task.id ?? 0);
                                        handleCloseMenu();
                                    }, onChangeState: (code) => {
                                        onChangeState(task.id ?? 0, code);
                                        handleCloseMenu();
                                    }, taskStates: taskStates, onClose: handleCloseMenu }))] }, task.id))) })] }), menuTask && (_jsx("div", { className: "fixed inset-0 z-40", onClick: handleCloseMenu }))] }));
};
export default TaskTable;
